import { useEffect, useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { Title, useGetList, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import {
  MADRASA_FEE_SETUP_CREATE,
  MADRASA_FEE_SETUP_DELETE,
  MADRASA_FEE_SETUP_EDIT,
  MADRASA_FEE_SETUP_VIEW,
} from "../shared/madrasa-permissions";
import { CopyFeesDialog } from "./CopyFeesDialog";
import { SyncDuesDialog } from "./SyncDuesDialog";
import {
  amountInputProblem,
  buildGridState,
  cellKey,
  defaultFrequency,
  diffGrid,
  firstGridProblem,
  RECURRING_FREQUENCIES,
  summarizeSave,
  type FeeHeadLite,
  type Frequency,
  type GridState,
  type StructureLite,
} from "./fee-grid-utils";

type YearRow = { id: string; name: string; isCurrent: boolean; isClosed: boolean };
type ClassRow = { id: string; name: string; sortOrder: number; isActive: boolean };
type HeadRow = FeeHeadLite & { isActive: boolean; sortOrder: number };
type SaveResult = {
  created: number;
  updated: number;
  removed: number;
  unchanged: number;
  problems: Array<{ classId: string; feeHeadId: string; message: string }>;
};

/** Class x fee-head grid for one academic year. Empty cell = no fee. */
export default function FeeStructuresGridPage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const canView = hasPermission(permissions, MADRASA_FEE_SETUP_VIEW);
  // The grid can add, change and remove fees, and the API needs all three rights for it.
  const canSave =
    hasPermission(permissions, MADRASA_FEE_SETUP_CREATE) &&
    hasPermission(permissions, MADRASA_FEE_SETUP_EDIT) &&
    hasPermission(permissions, MADRASA_FEE_SETUP_DELETE);

  const years = useGetList<YearRow>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const classes = useGetList<ClassRow>("madrasaClasses", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });
  const heads = useGetList<HeadRow>("feeHeads", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });

  const [yearId, setYearId] = useState("");
  useEffect(() => {
    if (!yearId && years.data?.length) {
      setYearId((years.data.find((y) => y.isCurrent) ?? years.data[0]).id);
    }
  }, [years.data, yearId]);
  const year = years.data?.find((y) => y.id === yearId);

  const structures = useGetList<StructureLite & { id: string }>(
    "feeStructures",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "className", order: "ASC" },
      filter: { academicYearId: yearId },
    },
    { enabled: Boolean(yearId) }
  );

  const [grid, setGrid] = useState<GridState>({});
  const [initial, setInitial] = useState<GridState>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SaveResult | null>(null);
  const [copying, setCopying] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // (Re)load the editable grid whenever the year or its stored fees change.
  useEffect(() => {
    if (structures.data) {
      const built = buildGridState(structures.data);
      setInitial(built);
      setGrid(built);
      setResult(null);
    }
  }, [structures.data]);

  const storedRows = useMemo(() => structures.data ?? [], [structures.data]);
  const rowClassIds = useMemo(() => new Set(storedRows.map((r) => r.classId)), [storedRows]);
  const rowHeadIds = useMemo(() => new Set(storedRows.map((r) => r.feeHeadId)), [storedRows]);
  // Show active ones, plus inactive ones that still carry fees (so they can be edited or cleared).
  const shownClasses = (classes.data ?? []).filter((c) => c.isActive || rowClassIds.has(c.id));
  const shownHeads = (heads.data ?? []).filter((h) => h.isActive || rowHeadIds.has(h.id));

  const changes = useMemo(
    () =>
      diffGrid(
        initial,
        grid,
        shownHeads,
        shownClasses.map((c) => c.id)
      ),
    [initial, grid, shownHeads, shownClasses]
  );
  const problem = firstGridProblem(grid);
  const readOnly = !canSave || !year || year.isClosed;

  const setCell = (
    classId: string,
    head: FeeHeadLite,
    patch: Partial<{ amount: string; frequency: Frequency }>
  ) => {
    const key = cellKey(classId, head.id);
    setGrid((current) => ({
      ...current,
      [key]: {
        amount: current[key]?.amount ?? "",
        frequency: current[key]?.frequency ?? defaultFrequency(head),
        ...patch,
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await callApi({
        location: "feeStructures/bulk-upsert",
        method: "POST",
        data: { academicYearId: yearId, cells: changes },
      });
      const r = response.data as SaveResult;
      setResult(r);
      notify(summarizeSave(r), { type: r.problems.length ? "warning" : "success" });
      structures.refetch();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not save the fees"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) return null;
  const loading = years.isPending || classes.isPending || heads.isPending;
  const nameOf = (id: string, list: Array<{ id: string; name: string }> | undefined) =>
    list?.find((x) => x.id === id)?.name ?? "?";

  return (
    <>
      <Title title="Fee structures" />
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Fee structures
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set what each class pays for each fee, and how often. The amount is per billing period
          (for example per month if monthly). Leave a box empty for no fee.
        </Typography>

        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 2, alignItems: { sm: "center" } }}
            >
              <TextField
                select
                label="Academic year"
                value={yearId}
                onChange={(e) => setYearId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                {(years.data ?? []).map((y) => (
                  <MenuItem key={y.id} value={y.id}>
                    {y.name}
                    {y.isCurrent ? " (current)" : ""}
                    {y.isClosed ? " (closed)" : ""}
                  </MenuItem>
                ))}
              </TextField>
              {!readOnly && (
                <Button
                  onClick={() => setCopying(true)}
                  disabled={saving}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy from another year
                </Button>
              )}
              {!readOnly && (
                <Button
                  onClick={() => setSyncing(true)}
                  disabled={saving || changes.length > 0}
                  startIcon={<SyncIcon />}
                  title={changes.length > 0 ? "Save your fee changes first" : undefined}
                >
                  Sync dues with these fees
                </Button>
              )}
            </Stack>

            {!canSave && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You can view fees but not change them.
              </Alert>
            )}
            {year?.isClosed && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {year.name} is closed, so its fees are read-only. Reopen the year to change them.
              </Alert>
            )}
            {shownClasses.length === 0 || shownHeads.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {shownHeads.length === 0 ? (
                  <>
                    Add <Link to="/feeHeads">fee heads</Link> first
                  </>
                ) : (
                  <>
                    Add <Link to="/madrasaClasses">classes</Link> first
                  </>
                )}
                , then set the fees here.
              </Alert>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2, maxHeight: "60vh" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: "sticky", left: 0, zIndex: 3, minWidth: 140 }}>
                        Class
                      </TableCell>
                      {shownHeads.map((h) => (
                        <TableCell key={h.id} sx={{ minWidth: 190 }}>
                          {h.name}
                          {h.isOneTime && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {" "}
                              (one-time)
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shownClasses.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                            fontWeight: 500,
                          }}
                        >
                          {c.name}
                        </TableCell>
                        {shownHeads.map((h) => {
                          const cell = grid[cellKey(c.id, h.id)];
                          const bad = amountInputProblem(cell?.amount ?? "");
                          return (
                            <TableCell key={h.id}>
                              <Stack direction="row" spacing={0.5}>
                                <TextField
                                  size="small"
                                  value={cell?.amount ?? ""}
                                  onChange={(e) => setCell(c.id, h, { amount: e.target.value })}
                                  disabled={readOnly || saving}
                                  error={Boolean(bad)}
                                  title={bad ?? undefined}
                                  placeholder="₹"
                                  slotProps={{
                                    htmlInput: {
                                      inputMode: "numeric",
                                      "aria-label": `${c.name} ${h.name} amount`,
                                    },
                                  }}
                                  sx={{ width: h.isOneTime ? 120 : 84 }}
                                />
                                {!h.isOneTime && (
                                  <TextField
                                    select
                                    size="small"
                                    value={cell?.frequency ?? defaultFrequency(h)}
                                    onChange={(e) =>
                                      setCell(c.id, h, { frequency: e.target.value as Frequency })
                                    }
                                    disabled={readOnly || saving || !(cell?.amount ?? "").trim()}
                                    slotProps={{
                                      htmlInput: { "aria-label": `${c.name} ${h.name} frequency` },
                                    }}
                                    sx={{ width: 108 }}
                                  >
                                    {RECURRING_FREQUENCIES.map((f) => (
                                      <MenuItem key={f.id} value={f.id}>
                                        {f.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              </Stack>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!readOnly && shownClasses.length > 0 && shownHeads.length > 0 && (
              <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={save}
                  disabled={saving || changes.length === 0 || Boolean(problem)}
                >
                  {saving
                    ? "Saving…"
                    : changes.length
                      ? `Save ${changes.length} ${changes.length === 1 ? "change" : "changes"}`
                      : "Save"}
                </Button>
                {problem && (
                  <Typography color="error" variant="body2">
                    {problem}
                  </Typography>
                )}
                {!problem && changes.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    The dues already generated for students are not changed until you sync them.
                  </Typography>
                )}
              </Stack>
            )}

            {result && result.problems.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These could not be saved:
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {result.problems.map((p) => (
                    <li key={`${p.classId}|${p.feeHeadId}`}>
                      {nameOf(p.classId, classes.data)} / {nameOf(p.feeHeadId, heads.data)}:{" "}
                      {p.message}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </>
        )}
      </Box>

      <SyncDuesDialog
        open={syncing}
        onClose={() => setSyncing(false)}
        yearId={yearId}
        yearName={year?.name ?? ""}
        classes={(classes.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
      />
      <CopyFeesDialog
        open={copying}
        onClose={() => setCopying(false)}
        onCopied={() => structures.refetch()}
        toYear={year}
        years={years.data ?? []}
      />
    </>
  );
}
